import * as React from "react";
import Tile from "./elements/Tile";
import Top from "./elements/Top";
import Bottom from "./elements/Bottom";
import { Meta } from "./elements/Typo";
import Icon from "../../elements/Icon";
import styled from "styled-components";
import PreviewInput from "../PreviewInput";
import PreviewTextarea from "../PreviewTextarea";
import withAutoHeightInput from "../../hocs/withAutoHeightInput";
import { select, useSelect } from "@hubper/theme";
import Button from "../../elements/Button";
import Text from "../../elements/Text";
import Spinner from "../../elements/Spinner";
import WithTooltip from "../WithTooltip";

const preventDefault = (evt: any) => {
  evt.preventDefault();
  evt.stopPropagation();
};

const RemoveOverlayBase = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: ${select.space(4)};
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1;
  text-align: center;
  overflow-y: auto;
`;

type RemoveOverlayProps = {
  onClose: () => void;
  onDelete: () => void;
  texts: {
    removePart: string;
    removePartDescription: string;
    delete: string;
    cancel: string;
  };
};

function RemoveOverlay({ onClose, onDelete, texts }: RemoveOverlayProps) {
  const { space } = useSelect();
  return (
    <RemoveOverlayBase onClick={preventDefault}>
      <Text c="white" fw="bold" fs={1} style={{ marginBottom: space(1) }}>
        {texts.removePart}
      </Text>
      <Text c="white" fs={-2} style={{ marginBottom: space(4) }}>
        {texts.removePartDescription}
      </Text>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Button
          type="grey"
          style={{ marginBottom: space(0) }}
          text={texts.cancel}
          onClick={onClose}
        />
        <Button
          type="color"
          c={["primary", 5]}
          text={texts.delete}
          onClick={onDelete}
        />
      </div>
    </RemoveOverlayBase>
  );
}

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${select.space(0)};
`;

const TopOverlay = styled.div<{ visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  content: "";
  background: rgba(255, 255, 255, 0.75);
  opacity: 0;
  transition: ${select.speed("slow")};

  :hover {
    opacity: 1;
  }
`;

type Texts = {
  enterTitle: string;
  enterDescription: string;
  parts: string;
  addImage: string;
  changeImage: string;
  active: string;
  inActive: string;
  removePart: string;
  removePartDescription: string;
  cancel: string;
  delete: string;
};

const DEFAULT_TEXTS: Texts = {
  enterTitle: "Voer titel in",
  enterDescription: "Voer omschrijving in",
  parts: "onderdelen",
  addImage: "Afbeelding toevoegen",
  changeImage: "Afbeelding aanpassen",
  cancel: "Annuleren",
  delete: "Verwijderen",
  active: "actief",
  inActive: "inactief",
  removePart: "Onderdeel verwijderen",
  removePartDescription: "Lorem ipsum dolor consectetur adipiscing elit"
};

const PreviewTextareaWithAutoHeight = withAutoHeightInput(PreviewTextarea);

type Props = {
  style?: React.CSSProperties;
  className?: string;
  id: string;
  title: string;
  description: string;
  imageSrc: string;
  nrOfContentParts: number;
  active: boolean;
  texts?: Texts;
  uploading?: boolean;
  onClick: () => void;
  onImageChange: () => void;
  onDelete: (id: string) => void;
  onChange: (id: string, partial: any) => void;
  onSave: (id: string, partial: any) => void;
};

function PartTileAdmin(
  // @ts-ignore
  {
    style,
    className,
    imageSrc = "",
    id,
    title = "",
    description = "",
    onClick,
    nrOfContentParts = 0,
    texts = DEFAULT_TEXTS,
    uploading,
    onChange,
    onSave,
    active,
    onDelete,
    onImageChange
  }: Props,
  ref: any
) {
  const [removeOverlay, setRemoveOverlay] = React.useState(false);

  return (
    <Tile
      ref={ref}
      style={{ overflow: "hidden", opacity: active ? 1 : 0.5, ...style }}
      className={className}
      onClick={onClick}
    >
      {removeOverlay && (
        <RemoveOverlay
          texts={texts}
          onClose={() => setRemoveOverlay(false)}
          onDelete={() => onDelete(id)}
        />
      )}
      <Top imageSrc={imageSrc} style={{ overflow: "hidden" }}>
        <TopOverlay visible={!imageSrc}>
          {uploading ? (
            <Spinner size={32} />
          ) : (
            <Button
              type="color"
              text={imageSrc ? texts.changeImage : texts.addImage}
              onClick={(evt: any) => {
                preventDefault(evt);
                onImageChange();
              }}
            />
          )}
        </TopOverlay>
      </Top>
      <Bottom
        topLeft={
          <>
            <TitleWrapper>
              <PreviewInput
                value={title}
                placeholder={texts.enterTitle}
                onClick={preventDefault}
                error={!title}
                onChange={(evt: any) => {
                  evt.persist();
                  onChange(id, { title: evt.target.value });
                }}
                onBlur={evt => {
                  evt.persist();
                  onSave(id, { title: evt.target.value });
                }}
                textProps={{
                  fs: 0,
                  fw: "bold",
                  c: ["grey", 9],
                  style: {
                    display: "block",
                    width: "100%",
                    padding: 0,
                    lineHeight: "1em"
                  }
                }}
              />
              <div style={{ display: "flex", flexShrink: 0 }}>
                <WithTooltip
                  key={active ? "visible" : "notVisible"}
                  tooltipWidth={200}
                  content={active ? texts.active : texts.inActive}
                >
                  <Icon
                    name={active ? "visible" : "notVisible"}
                    size={16}
                    c={["grey", 5]}
                    onClick={(evt: any) => {
                      preventDefault(evt);
                      onChange(id, { active: !active });
                      onSave(id, { active: !active });
                    }}
                    style={{ padding: 3, boxSizing: "content-box" }}
                  />
                </WithTooltip>
                <Icon
                  name="delete"
                  size={16}
                  c={["grey", 5]}
                  style={{ padding: 3, boxSizing: "content-box" }}
                  onClick={(evt: any) => {
                    preventDefault(evt);
                    setRemoveOverlay(true);
                  }}
                />
              </div>
            </TitleWrapper>
            <PreviewTextareaWithAutoHeight
              value={description}
              placeholder={texts.enterDescription}
              onClick={preventDefault}
              onChange={(evt: any) => {
                evt.persist();
                onChange(id, { description: evt.target.value });
              }}
              onBlur={(evt: any) => {
                evt.persist();
                onSave(id, { description: evt.target.value });
              }}
              maxLength={125}
              textProps={{
                fs: -2,
                fw: "bold",
                c: ["grey", 5],
                style: {
                  display: "block",
                  width: "100%",
                  padding: 0,
                  lineHeight: "1em",
                  resize: "none"
                }
              }}
            />
          </>
        }
        bottomRight={
          <Meta>
            {nrOfContentParts} {texts.parts}
          </Meta>
        }
      />
    </Tile>
  );
}

export default React.forwardRef(PartTileAdmin);
